import { createPortal } from 'react-dom';
import { Button, Space, Table, Typography } from '@arco-design/web-react';
import React, { useMemo, useState } from 'react';
import { Tooltip } from '@bay-email/editor';
import BayToast from '@bay-ui/BayToast';
import { SingleListingDrawer } from './SingleListingDrawer';
import { t } from '@bay-email/core';
import { getListingImageUrl } from '../../../../utils/getListingImageUrl';

interface ListingItem {
	id: string;
	title?: string;
	address?: string;
	city?: string;
	stateProvince?: string;
	postalCode?: string;
	list_price?: number;
	beds?: number;
	baths?: number;
	sq_foot?: number;
	image_data?: string[];
}

interface SingleListingPickerProps {
	listings: ListingItem[];
	selectedListing?: ListingItem | null;
	total: number;
	size: number;
	loading: boolean;
	onSelect: (selectedListing: ListingItem | null) => void;
	onPaginationChange: (params: {
		page: number;
		size: number;
		keyword?: string;
		filters?: {
			startdate?: string;
			enddate?: string;
			propertytype?: string[];
			pricemin?: string;
			pricemax?: string;
			status?: string[];
		};
		sortBy?: string;
		orderDirection?: 'ASC' | 'DESC';
	}) => void;
	disabled?: boolean;
	/**
	 * Tooltip shown on the disabled Select Listing button in Design Manager.
	 * The wording names the scope the listing will fill, so it differs per
	 * caller — wrapper, listing column, or an individual block that inherits
	 * from one. Callers pass their own; this is the generic fallback.
	 */
	disabledTooltip?: string;
	/**
	 * The block's saved listing is being fetched. Distinct from `loading`, which
	 * is the drawer's own search — this one means the panel cannot yet say
	 * whether a listing is attached, so it must not claim none is.
	 */
	resolving?: boolean;
	/** Forwarded to the drawer via the props spread below. */
	onOpen?: () => void;
	openSignal?: number;
	setActiveView?: (activeView: {
		id: 'mylistings' | 'companylistings';
		page: number;
		size: number;
	}) => void;
}

export function SingleListingPicker(props: SingleListingPickerProps) {
	const selectedListing = useMemo(
		() => props.selectedListing || null,
		[props.selectedListing],
	);
	// Toast lives here, not in the drawer: the drawer trigger moves in the tree
	// once a listing is picked, remounting the drawer and dropping its state.
	const [toast, setToast] = useState<boolean>(false);

	// Check if we have a valid selected listing (not just a placeholder)
	const hasValidSelection =
		selectedListing && selectedListing.id && selectedListing.address;

	const columns = [
		{
			title: t('Content'),
			className: 'p-0 border-0',
			render: (_: any, record: ListingItem) => {
				const imageUrl = getListingImageUrl(record.image_data);

				// Format address
				const streetAddress = [record.address].filter(Boolean).join(', ');
				const displayText = streetAddress || record.title || `Listing ${record.id}`;

				// Format price
				const price =
					typeof record.list_price === 'number'
						? `$${record.list_price.toLocaleString()}`
						: record.list_price || '$0';

				return (
					<Space>
						<div
							style={{
								width: 40,
								height: 40,
								backgroundImage: `url(${imageUrl})`,
								backgroundSize: '100%',
								backgroundRepeat: 'no-repeat',
								backgroundPosition: 'center',
								border: '1px solid #e8e8e8',
							}}
						/>
						<div>
							<Tooltip content={displayText} placement="top" >
								<Typography.Text
									ellipsis
									className='m-0 pe-auto'
									style={{ width: 150, display: 'block' }}
									// Arco auto-sets a native `title` on truncated text unless told
									// otherwise, which pops its own browser tooltip alongside ours.
									// Empty string wins the merge inside Typography's Base component
									// (rest props are applied after its internal title), suppressing it.
									title=""
								>
									{displayText}
								</Typography.Text>
							</Tooltip>
							<Typography.Text
								type='secondary'
								style={{ fontSize: '12px' }}
							>
								{price}
							</Typography.Text>
						</div>
					</Space>
				);
			},
		},
	];

	// Simple table without drag/drop functionality (no drag handle needed for single item)
	const components = {
		body: {
			tbody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
				<tbody
					{...props}
					ref={ref}
				/>
			)),
			row: React.forwardRef<HTMLTableRowElement>((props, ref) => (
				<tr
					{...props}
					ref={ref}
				/>
			)),
		},
	};

	const listingDrawerTrigger = (
		<SingleListingDrawer
			{...props}
			onSelect={listing => {
				props.onSelect(listing);
				if (listing) setToast(true);
			}}
			selectedListing={selectedListing}
			disabled={props.disabled}
			openSignal={props.openSignal}
			setActiveView={props.setActiveView}
		>
			<Tooltip
				content={
					props.disabled
						? props.disabledTooltip ||
							t(
								"Users choosing this template will be able to select a listing to auto-fill this block's listing dynamic fields. Each block can feature different listings.",
							)
						: ''
				}
				disabled={!props.disabled}
			>
				<div style={{ width: '100%' }}>
					<Button
						className={
							hasValidSelection
								? 'rounded-1 w-100 border-0 p-0'
								: 'rounded-1 w-100'
						}
						type={hasValidSelection ? 'outline' : 'primary'}
						disabled={props.disabled}
					>
						{hasValidSelection ? t('Change Listing') : t('Select Listing')}
					</Button>
				</div>
			</Tooltip>
		</SingleListingDrawer>
	);

	// Nothing is known yet, so neither the "pick one" prompt nor the summary row
	// would be honest. Reopening a design used to spend the whole lookup showing
	// the empty state for a listing it already had.
	if (props.resolving && !hasValidSelection) {
		return (
			<div className='px-3 py-4'>
				<div className='p-3 bg-light lh-sm rounded-1 text-muted'>
					{t('Loading the listing attached to this block…')}
				</div>
			</div>
		);
	}

	return (
		<>
			<div className='px-3 py-4'>
				{hasValidSelection && !props.disabled ? (
					<div className='d-flex justify-content-between'>
						<Table
							loading={props.loading}
							rowKey='id'
							showHeader={false}
							border={false}
							className='arco-drag-table-container-listings pe-none'
							components={components}
							columns={columns}
							data={[selectedListing]} // Show only the selected listing
							pagination={false}
						/>
						{listingDrawerTrigger}
					</div>
				) : !props.disabled && !hasValidSelection ? (
					<div className='p-3 bg-yellow-100 border-yellow-200 lh-sm rounded-1 mb-3'>
						{t('This block contains dynamic listing fields. Select a listing to automatically pull in the correct property data.')}
					</div>
				) : null}
				{!hasValidSelection && listingDrawerTrigger}
			</div>
			
			{createPortal(
				<BayToast
					show={toast}
					onClose={() => setToast(false)}
					text={t('Listing information added successfully!')}
					variant='success'
				/>,
				document.body,
			)}
		</>
	);
}
